import React, { useState } from 'react';
import IconGlyph from './IconGlyph';
import IconPickerModal from './IconPickerModal';
import RateSeparatorLabel from './RateSeparatorLabel';
import { isValidHex } from '../utils/colorUtil';
import { normalizeIconValue } from '../utils/iconUtil';
import '../css/RateBandSeparatorSettings.css';

/**
 * The separator settings on one hourly rate (Config -> Sports & Courts ->
 * Hourly rates): an icon and a background colour for the separator row the
 * guest time slot grid draws where this rate starts, with a live preview.
 *
 * Both are optional. Without a colour the separator uses the club's accent
 * (Config -> Theme); without an icon it shows text only.
 *
 * @param {object} props
 * @param {object} props.band - the rate band ({ name, icon, color, ... })
 * @param {string} props.price - formatted price for the preview, e.g. "₱250/hr"
 * @param {string} props.hours - formatted hours for the preview
 * @param {string} props.design - the separator design in use (Config -> Theme)
 * @param {(field: 'icon'|'color', value: string) => void} props.onChange
 * @returns {JSX.Element}
 */
function RateBandSeparatorSettings({ band, price, hours, design, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const hasColor = isValidHex(band?.color);
  const label = (band?.name || '').trim() || 'this rate';

  return (
    <div className="rbs-row">
      <span className="rbs-caption">Separator</span>

      <button
        type="button"
        className={`rbs-icon-btn${band?.icon ? ' set' : ''}`}
        onClick={() => setPickerOpen(true)}
        title={band?.icon ? 'Change icon' : 'Add icon'}
      >
        {band?.icon ? <IconGlyph icon={band.icon} /> : <span className="rbs-icon-plus">+</span>}
        <span>{band?.icon ? 'Icon' : 'Add icon'}</span>
      </button>

      <label className="rbs-color">
        <input
          type="color"
          value={hasColor ? band.color : '#ffffff'}
          onChange={(e) => onChange('color', e.target.value)}
          aria-label={`Separator color for ${label}`}
        />
        <span>{hasColor ? band.color : 'Default color'}</span>
      </label>
      {hasColor && (
        <button type="button" className="rbs-reset" onClick={() => onChange('color', '')}>
          Use default
        </button>
      )}

      <span className="rbs-preview" aria-label="Separator preview">
        <RateSeparatorLabel
          design={design}
          name={band?.name}
          icon={band?.icon}
          color={band?.color}
          price={price}
          hours={hours}
        />
      </span>

      {pickerOpen && (
        <IconPickerModal
          title={`Icon for ${label}`}
          value={band?.icon || ''}
          onSave={(icon) => { onChange('icon', normalizeIconValue(icon)); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

export default RateBandSeparatorSettings;
